import * as THREE from 'three';

/* Lifted out of its IIFE unchanged but for one thing: THREE used to be passed
   in as the first argument, because inside an IIFE there was no other way to
   reach it. With a bundler that indirection buys nothing, so it is imported
   here and every call site drops the argument. */

/* PICTURE PLANES — flat or solid, whole or cut to a polygon.

   points: array of [u,v] in normalised IMAGE space — u left→right, v top→bottom,
   both 0..1. The plane spans [-w/2,w/2] x [-h/2,h/2] with the texture unflipped,
   so image-top (v=0) maps to the plane top (y=+h/2, UV v=1).

   invert=false  → the polygon is the KEPT region (everything else disappears).
   invert=true   → the polygon is a HOLE punched out of the full plane.

   UVs are always recomputed from vertex positions so the image never shifts as
   the silhouette changes. */

/** The outline: the full rectangle, the polygon, or the rectangle with the
 *  polygon punched out of it. Shared so a flat plane and an extruded one are
 *  cut to exactly the same silhouette. */
function panelShape(points, w, h, invert){
  const toXY = ([u,v]) => [ (u - 0.5) * w, (0.5 - v) * h ];
  const hw = w/2, hh = h/2;
  const rect = s => { s.moveTo(-hw,-hh); s.lineTo(hw,-hh); s.lineTo(hw,hh); s.lineTo(-hw,hh); s.closePath(); };
  const poly = (s, pts) => {
    const p0 = toXY(pts[0]);
    s.moveTo(p0[0], p0[1]);
    for(let i=1;i<pts.length;i++){ const p = toXY(pts[i]); s.lineTo(p[0], p[1]); }
    s.closePath();
  };

  const shape = new THREE.Shape();
  const cut = points && points.length >= 3;
  if(cut && !invert){ poly(shape, points); return shape; }
  rect(shape);
  if(cut && invert){ const hole = new THREE.Path(); poly(hole, points); shape.holes.push(hole); }
  return shape;
}

/* Recompute UVs from positions: x∈[-w/2,w/2] → u∈[0,1], y∈[-h/2,h/2] → v∈[0,1].
   Without this the pixels shift as the silhouette changes, and on an extruded
   panel ExtrudeGeometry's own UVs are in METRES rather than 0..1, which maps
   the picture to a corner of itself. The side walls are covered by a separate
   material (group 1) so their UVs are irrelevant. */
function remapUVs(geo, w, h){
  const pos = geo.attributes.position;
  const uv = new Float32Array(pos.count * 2);
  for(let i=0;i<pos.count;i++){
    uv[i*2]   = pos.getX(i)/w + 0.5;
    uv[i*2+1] = pos.getY(i)/h + 0.5;
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  return geo;
}

/**
 * A picture plane, optionally masked and optionally given real thickness.
 *
 * At depth 0 this is exactly the flat plane images have always used. Above it
 * the panel is extruded and bevelled the way 3D Text is, so a clip can read as
 * a physical screen set into the recess rather than a decal printed on the
 * back wall — which is the whole point of an anamorphic build. Two material
 * groups come out of ExtrudeGeometry for free (0 = the faces, 1 = the walls),
 * so the edge can take its own colour exactly as type's does.
 *
 * @param {{w:number,h:number,points?:Array,invert?:boolean,depth?:number,bevel?:number}} o
 */
export function panelGeometry({ w, h, points, invert, depth = 0, bevel = 0 }){
  const shape = panelShape(points, w, h, invert);
  if(!(depth > 1e-4)) {
    const flat = new THREE.ShapeGeometry(shape);
    remapUVs(flat, w, h);
    flat.computeVertexNormals();
    return flat;
  }

  /* ExtrudeGeometry grows the bevel OUTSIDE `depth`, spanning -bev … depth+bev,
     so the core is shrunk to make the total exactly what was asked for — the
     same correction buildTextGeometry makes, for the same reason.

     It also grows the SILHOUETTE outward by bevelSize, which is not corrected:
     a 2m panel with a 0.03 bevel is 2.06m across its widest ring. That matches
     what 3D Text's bevel already does, and inset-offsetting an arbitrary mask
     polygon is a different and much larger problem than it is worth here. The
     consequence to know is that the bevel ring's UVs land just outside 0..1 —
     the texture clamps there (scene/video.js sets that explicitly), so the
     edge pixels wrap the chamfer, which is what a real bezel looks like. */
  const bev = Math.max(0, Math.min(bevel, depth * .45, Math.min(w, h) * .25));
  const core = Math.max(5e-4, depth - 2*bev);
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: core, steps: 1, curveSegments: 1,
    bevelEnabled: bev > 1e-5,
    bevelThickness: bev, bevelSize: bev, bevelOffset: 0,
    bevelSegments: bev > 1e-5 ? 5 : 1
  });
  /* Centre on z so the panel straddles its own transform the way the flat
     plane did — otherwise adding depth would shunt the picture forward and
     look like the object moved. */
  geo.translate(0, 0, -(core + (bev > 1e-5 ? bev*2 : 0)) / 2);
  remapUVs(geo, w, h);
  geo.computeVertexNormals();
  geo.computeBoundingSphere();
  return geo;
}

/* Build the geometry for a masked image plane — the flat case of
   panelGeometry, kept as its own name because the null-for-too-few-points
   contract is what buildImage branches on. */
export function buildMaskGeometry(points, w, h, invert){
  if(!points || points.length < 3) return null;
  return panelGeometry({ w, h, points, invert });
}

/* Even-odd point-in-polygon, for tests/hit-tests. pt and poly in the same space. */
export function pointInPoly(pt, poly){
  let inside = false;
  for(let i=0,j=poly.length-1;i<poly.length;j=i++){
    const xi=poly[i][0], yi=poly[i][1], xj=poly[j][0], yj=poly[j][1];
    if(((yi>pt[1])!==(yj>pt[1])) && (pt[0] < (xj-xi)*(pt[1]-yi)/(yj-yi)+xi)) inside = !inside;
  }
  return inside;
}
